import React, { useState, useEffect, useCallback } from 'react'
import {
  FileText,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  AlertTriangle,
  TrendingUp,
  Activity,
  Database
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card'
import { Badge } from '../components/ui/Badge'
import { Button } from '../components/ui/Button'
import { AdminLayout } from '../components/admin/AdminLayout'
import { PDFMonitoringService, type PDFMetrics, type PDFJob, type PDFError, type QueueStatus } from '../services/pdfMonitoringService'

export function AdminPDFMonitoringPage() {
  // State
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [lastRefresh, setLastRefresh] = useState(new Date())

  const [metrics, setMetrics] = useState<PDFMetrics | null>(null)
  const [recentJobs, setRecentJobs] = useState<PDFJob[]>([])
  const [errors, setErrors] = useState<PDFError[]>([])
  const [queueStatus, setQueueStatus] = useState<QueueStatus | null>(null)

  // Auto-refresh interval (30 seconds)
  const REFRESH_INTERVAL = 30000

  // Load monitoring data
  const loadData = useCallback(async () => {
    try {
      setError(null)
      const data = await PDFMonitoringService.getAllData()

      if (data.hasError) {
        setError('Failed to load some monitoring data')
      }

      setMetrics(data.metrics)
      setRecentJobs(data.recentJobs)
      setErrors(data.errors)
      setQueueStatus(data.queueStatus)
      setLastRefresh(new Date())
    } catch (err: any) {
      setError(err.message || 'Failed to load monitoring data')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [])

  // Manual refresh
  const handleRefresh = useCallback(async () => {
    setRefreshing(true)
    await loadData()
  }, [loadData])

  // Initial load
  useEffect(() => {
    loadData()
  }, [loadData])

  // Auto-refresh
  useEffect(() => {
    const interval = setInterval(() => {
      loadData()
    }, REFRESH_INTERVAL)

    return () => clearInterval(interval)
  }, [loadData])

  // Helper functions
  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'success' | 'warning' | 'error' | 'info'> = {
      completed: 'success',
      processing: 'info',
      queued: 'warning',
      pending: 'warning',
      failed: 'error'
    }
    return <Badge variant={variants[status] || 'info'}>{status}</Badge>
  }

  const formatDuration = (minutes: number | undefined) => {
    if (!minutes) return 'N/A'
    if (minutes < 1) return '< 1 min'
    return `${minutes} min`
  }

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleString()
  }

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </AdminLayout>
    )
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">PDF Monitoring</h1>
            <p className="text-gray-600 mt-1">Monitor PDF generation queue and worker performance</p>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-500">
              Last updated: {lastRefresh.toLocaleTimeString()}
            </span>
            <Button
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            <span className="text-red-800">{error}</span>
          </div>
        )}

        {/* Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Queue Status</CardTitle>
              <Activity className="w-4 h-4 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{queueStatus?.queued || 0}</div>
              <p className="text-xs text-gray-600 mt-1">
                {queueStatus?.processing || 0} processing
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Completed (24h)</CardTitle>
              <CheckCircle className="w-4 h-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">
                {metrics?.last24Hours.completed || 0}
              </div>
              <p className="text-xs text-gray-600 mt-1">
                {metrics?.last24Hours.failed || 0} failed
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Avg Processing Time</CardTitle>
              <Clock className="w-4 h-4 text-purple-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {metrics?.performance.avgProcessingMinutes.toFixed(1) || 0} min
              </div>
              <p className="text-xs text-gray-600 mt-1">
                {metrics?.performance.avgRetryCount.toFixed(1) || 0} avg retries
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Total Completed</CardTitle>
              <TrendingUp className="w-4 h-4 text-indigo-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {metrics?.summary.completed || 0}
              </div>
              <p className="text-xs text-gray-600 mt-1">
                {metrics?.summary.failed || 0} failed total
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Recent Jobs */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Recent PDF Generation Jobs
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-4 text-sm font-medium text-gray-600">Tracking ID</th>
                    <th className="text-left py-2 px-4 text-sm font-medium text-gray-600">Status</th>
                    <th className="text-left py-2 px-4 text-sm font-medium text-gray-600">Queued At</th>
                    <th className="text-left py-2 px-4 text-sm font-medium text-gray-600">Duration</th>
                    <th className="text-left py-2 px-4 text-sm font-medium text-gray-600">Retries</th>
                    <th className="text-left py-2 px-4 text-sm font-medium text-gray-600">Error</th>
                  </tr>
                </thead>
                <tbody>
                  {recentJobs.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center py-8 text-gray-500">
                        No recent jobs found
                      </td>
                    </tr>
                  ) : (
                    recentJobs.map((job) => (
                      <tr key={job.tracking_id} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4 font-mono text-sm">{job.tracking_id}</td>
                        <td className="py-3 px-4">{getStatusBadge(job.status)}</td>
                        <td className="py-3 px-4 text-sm text-gray-600">{formatDate(job.queued_at)}</td>
                        <td className="py-3 px-4 text-sm">{formatDuration(job.processing_time_minutes)}</td>
                        <td className="py-3 px-4 text-sm">{job.retry_count}</td>
                        <td className="py-3 px-4 text-sm text-red-600 max-w-xs truncate">
                          {job.error || '-'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Error Analysis */}
        {errors.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <XCircle className="w-5 h-5 text-red-600" />
                Error Analysis
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {errors.map((error, idx) => (
                  <div key={idx} className="border-l-4 border-red-500 bg-red-50 p-4 rounded-r">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="font-medium text-red-900">{error.error_message}</p>
                        <p className="text-sm text-red-700 mt-1">
                          Occurred {error.count} time{error.count > 1 ? 's' : ''} • Last: {formatDate(error.last_occurrence)}
                        </p>
                        <p className="text-xs text-red-600 mt-2">
                          Affected IDs: {error.tracking_ids.slice(0, 3).join(', ')}
                          {error.tracking_ids.length > 3 && ` +${error.tracking_ids.length - 3} more`}
                        </p>
                      </div>
                      <Badge variant="error">{error.count}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  )
}
